import { DataStream, DisplayUnit, FlowTimeUnits, MeasurementUnit } from '@thingslog/repositories';
import { DisplayUnits } from '@thingslog/utils';
import { DataResultParsed, DataResultParseStream } from './DataResultParsed';

export const getStreamLabel = (stream: DataStream, fallback: string): string => {
  const { deviceName, deviceNumber, sensorName, sensorIndex } = stream.metaData;

  if (deviceName && sensorName) {
    return `${deviceName} - ${sensorName}`;
  }

  if (deviceNumber && sensorIndex) {
    return `${deviceNumber} - ${sensorIndex}`;
  }

  return sensorName || deviceName || deviceNumber || fallback;
};

export const getStreamUnit = (stream: DataStream): string | undefined => {
  const { unit, timeUnit } = stream.metaData;

  // When a time unit accompanies the sensor unit the reading is a flow (e.g.
  // LITER + SECONDS -> "l/s"); resolve the combined display unit rather than
  // dropping the time component.
  if (unit && timeUnit) {
    return DisplayUnits.getDisplayFlowUnit(unit as MeasurementUnit, timeUnit as FlowTimeUnits);
  }

  return unit || undefined;
};

export const formatDisplayUnit = (unit?: string): string => {
  if (!unit) {
    return '';
  }

  const displayUnit = DisplayUnit[unit as MeasurementUnit];

  return displayUnit !== undefined ? displayUnit : unit;
};

export const formatValueWithUnit = (value: number | string, unit?: string): string => {
  const formattedValue = typeof value === 'number' ? value.toString() : value;
  const displayUnit = formatDisplayUnit(unit);

  return displayUnit ? `${formattedValue} ${displayUnit}` : formattedValue;
};

export const applySingleUnitYAxisFormatter = (
  parsed: DataResultParsed,
  units: Set<string>
): void => {
  const uniqueUnits = Array.from(units);

  if (uniqueUnits.length !== 1 || !parsed.yAxis) {
    return;
  }

  const [unit] = uniqueUnits;
  parsed.yAxis.formatter = (value: number): string => formatValueWithUnit(value, unit);
};

export const createUnitTooltipFormatter = (): ((
  value: number | string,
  name: string,
  dataStreams: DataResultParseStream[]
) => [string, string]) => {
  return (
    value: number | string,
    name: string,
    dataStreams: DataResultParseStream[]
  ): [string, string] => {
    let unitForStream: string | undefined;

    dataStreams.forEach((stream: DataResultParseStream) => {
      stream.visualization.forEach((visualization: { name?: string; unit?: string }) => {
        if (visualization.name === name && visualization.unit) {
          unitForStream = visualization.unit;
        }
      });
    });

    return [formatValueWithUnit(value, unitForStream), name];
  };
};
